import { Menu, BasicOrderMealItemWithContents, MenuItem, PortionItemType } from '@/models';
import { findEntreeInMenu, isDefined, isPortionItemType } from '@/helpers';
import { Logger } from '../logger';
import { UtilsService } from '../utils/utils.service';
import { MenuService } from '../menu/menu.service';

export interface EntreeService {
  getFormattedDetails(orderEntree: BasicOrderMealItemWithContents, menu: Menu): string;
  getIndividualContentDetails(orderEntree: BasicOrderMealItemWithContents, menu: Menu): string[];
}

export class AppEntreeService implements EntreeService {
  constructor(private logger: Logger, private utils: UtilsService, private menu: MenuService) {}

  getFormattedDetails(orderEntree: BasicOrderMealItemWithContents, menu: Menu) {
    const detailsParts = this.getIndividualContentDetails(orderEntree, menu);

    switch (detailsParts.length) {
      case 1:
        return detailsParts[0];
      case 2:
        return `${detailsParts[0]} and ${detailsParts[1]}`;
      default:
        const formattedDetailsParts = [...detailsParts.slice(0, detailsParts.length - 1), `and ${detailsParts[detailsParts.length - 1]}`];

        return formattedDetailsParts.join(', ');
    }
  }

  getIndividualContentDetails(orderEntree: BasicOrderMealItemWithContents, menu: Menu): string[] {
    const menuEntree = findEntreeInMenu(orderEntree.menuItemId, menu);
    if (!menuEntree) {
      return [];
    }

    const menuEntreeContents = menuEntree.contents || [];
    const allContents = [];
    const allEntrees = menu.entrees || [];

    for (const item of orderEntree.contents || []) {
      const menuItem = menuEntreeContents.find(c => c.itemId == item.menuItemId);
      if (menuItem) {
        allContents.push({ item, menuItem });
      }
    }

    const portions = allContents.filter(i => isPortionItemType(i.menuItem.itemType));
    const contents = allContents.filter(i => !portions.find(p => p.item.menuItemId == i.item.menuItemId));

    const formattedPortion = portions.length ? this.formatPortion(portions[0].menuItem, orderEntree.menuItemId) : '';

    const portionParts = [
      menuEntree.primaryFillingName,
      ...portions
        .filter(p => p.menuItem.pricingReferenceItemId != orderEntree.menuItemId)
        .map(p => {
          const referencedItem = allEntrees.find(e => e.itemId == p.menuItem.pricingReferenceItemId);
          if (!referencedItem) {
            this.logger.warn('Failed to find referenced item in entree contents', {
              entreeId: menuEntree.itemId,
              pricingReferenceItemId: p.menuItem.pricingReferenceItemId,
            });

            return;
          }

          return referencedItem.primaryFillingName;
        })
        .filter(isDefined),
    ];

    const detailsParts = [
      ...portionParts.map(portion => `${portion}${formattedPortion ? ` (${formattedPortion})` : ''}`),
      ...contents.map(({ item, menuItem }) => {
        const name = menuItem.itemName;

        const modifiers = [];

        const menuItemPrice = this.menu.getItemPrice(menuItem);
        if (menuItemPrice) {
          modifiers.push(this.utils.formatPrice(menuItemPrice));
        }

        const customization = item.customizationId && (menuItem.customizations || []).find(c => c.id == item.customizationId);
        if (customization) {
          modifiers.push(this.formatCustomization(customization.name));
        }

        return `${name}${modifiers.length ? ` (${modifiers.join(', ')})` : ''}`;
      }),
    ];

    return detailsParts;
  }

  private formatCustomization(customizationName: string): string {
    switch (customizationName) {
      case 'Half':
        return '½';
      default:
        return customizationName;
    }
  }

  private formatPortion(portion: MenuItem, variantItemId: string): string {
    switch (portion.itemType) {
      case PortionItemType.HalfPortion:
        return '½';
      case PortionItemType.ExtraPortion:
        // If the portion is for the variant, then it's a 2x -- otherwise we're distributing the extra portion to a different variant
        return portion.pricingReferenceItemId == variantItemId ? '2x' : '1x';
      default:
        return portion.itemType;
    }
  }
}
